/**
 * Statisches Demo-Projekt für den öffentlichen `/demo`-Modus.
 *
 * Dieses Projekt wird komplett clientseitig geladen — ohne Login, ohne Backend
 * und ohne Speichern. Änderungen bleiben nur in der aktuellen Session bestehen.
 *
 * WICHTIG: Diese Datei ergänzt die bestehende Implementierung, sie verändert
 * sie nicht. Das Demo-Projekt ist bewusst getrennt vom regulären
 * Projekt-/Namespace-Flow gehalten.
 */

import type { TreeFile, TreeFolder } from './projectService'

/** Eindeutige ID des Demo-Projekts (auch als Sidebar-Root-Ordner-ID genutzt). */
export const DEMO_PROJECT_ID = 'demo'

/** Anzeigename des Namespaces/Bereichs in der Sidebar. */
export const DEMO_NAMESPACE_NAME = 'Demo-Projekt'

export interface DemoFile {
  /** Voller Pfad = IDE-Dateiname (Teil des fileStore-Keys via fileUid), z.B. "Rechner.java" */
  path: string
  /** Anzeigename (letztes Pfadsegment) */
  name: string
  /** Java-Quelltext */
  content: string
}

/**
 * Die Beispieldateien des Demo-Projekts.
 * Ein kleines, lauffähiges Java-Beispiel, das Konsole, Klassen und Schleifen zeigt.
 */
export const DEMO_FILES: DemoFile[] = [
  {
    path: 'Main.java',
    name: 'Main.java',
    content: `/*
 * Willkommen bei CodeLab! 👋
 *
 * Das hier ist eine interaktive DEMO. Du kannst den Code frei bearbeiten
 * und mit dem Play-Button (▶) ausführen.
 *
 * Hinweis: Es ist kein Login nötig und deine Änderungen werden NICHT
 * gespeichert – sie verschwinden, sobald du die Seite neu lädst.
 */
public class Main {

   public static void main(String[] args) {
      System.out.println("Hallo, willkommen bei CodeLab!");
      System.out.println("---------------------------------");

      // Wir nutzen die zweite Datei "Rechner.java" aus dem Dateibaum links.
      Rechner rechner = new Rechner();

      int summe = rechner.addiere(3, 4);
      System.out.println("3 + 4 = " + summe);

      int produkt = rechner.multipliziere(6, 7);
      System.out.println("6 * 7 = " + produkt);

      System.out.println("---------------------------------");
      System.out.println("Kleine Zaehlschleife:");
      for (int i = 1; i <= 5; i++) {
         System.out.println("  Schritt " + i);
      }

      System.out.println("Viel Spass beim Ausprobieren! 🚀");
   }
}
`,
  },
  {
    path: 'Rechner.java',
    name: 'Rechner.java',
    content: `/*
 * Eine kleine Hilfsklasse, die von Main.java verwendet wird.
 * Aendere ruhig die Methoden und schaue, wie sich die Ausgabe veraendert.
 */
public class Rechner {

   public int addiere(int a, int b) {
      return a + b;
   }

   public int multipliziere(int a, int b) {
      return a * b;
   }
}
`,
  },
]

/**
 * Baut aus {@link DEMO_FILES} den Sidebar-Baum und die fileStore-Inhalte auf.
 * Frische Kopien der Inhalte, damit ein Reset die Originale wiederherstellen kann.
 */
export function buildDemoTree(): { root: TreeFolder; contents: Record<string, string> } {
  const files: TreeFile[] = DEMO_FILES.map((f) => ({ path: f.path, name: f.name }))

  const projectFolder: TreeFolder = {
    id: DEMO_PROJECT_ID,
    name: 'Erste Schritte',
    folders: [],
    files,
  }

  const root: TreeFolder = {
    id: `${DEMO_PROJECT_ID}-namespace`,
    name: DEMO_NAMESPACE_NAME,
    folders: [projectFolder],
    files: [],
  }

  const contents: Record<string, string> = {}
  for (const f of DEMO_FILES) {
    contents[f.path] = f.content
  }

  return { root, contents }
}
